///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Draws the whole webcam catalog as one scene node. Each frame it picks a
/// cluster tier from the camera's ground scale (with hysteresis), culls that
/// tier's items against the horizon and the viewport, and appends the
/// survivors to the shared instanced batches. Nothing is allocated per
/// camera, so the catalog size no longer matters. Three marker forms:
///
/// - thumbnail chip: a single non-traffic camera, or a cluster whose
///   best-ranked camera is non-traffic (ranking prefers them, so any
///   cluster with a scenic / marine / ... camera shows its picture). The
///   frame comes from the shared WebcamThumbAtlas; clusters carry their
///   count on the chip's corner
/// - ember dome: a single traffic camera (infrastructure, not a picture
///   worth a chip; the hover card shows its frame), drawn exactly like
///   WebcamNode's dome
/// - count badge: an all-traffic cluster
///
/// Tier changes cross-fade over cTransitionMs with children growing out of
/// their cluster (zooming in) or collapsing into it (zooming out), using the
/// parent links in the index. Items that are the same in both tiers (a lone
/// camera passed up unchanged) are drawn once at full opacity, so nothing
/// blinks at a boundary.
///
/// Picking is screen-space over the items drawn last frame. A hit on a
/// camera whose row is cached materializes a real WebcamNode through the
/// host and hands the hit to it, so hover cards, taps and focus run the
/// ordinary entity path; a hit on a cluster is handled here (tap zooms to
/// fit the cluster).
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { type EmberDomeAppearance, type EmberDomeBatch, sharedEmberDomeBatch } from "@/earthbrowser/entities/EmberDome/EmberDomeBatch"
import { celestialToWorldVector, greenwichSiderealTime, sunPosition } from "@/earthbrowser/utils/astronomy"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { datasetUrl } from "@/utils/datasetUrl"
import type { WebcamEntry } from "@shared-types/webcam"
import { requestRender } from "@stovokor/engine/RenderRequest"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { GestureState, type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { HoverGestureRecognizer } from "@stovokor/gestures/HoverGestureRecognizer"
import { LongPressGestureRecognizer } from "@stovokor/gestures/LongPressGestureRecognizer"
import { TapGestureRecognizer } from "@stovokor/gestures/TapGestureRecognizer"
import { rgba, type color } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { vec3 } from "@stovokor/math/vector"
import { computeViewportBBox } from "../viewport-bbox"
import { packCount, sharedWebcamBadgeBatch, type WebcamBadgeBatch } from "./WebcamBadgeBatch"
import { cChipFlagLive, cChipFlagStale, sharedWebcamChipBatch, type WebcamChipBatch } from "./WebcamChipBatch"
import { sharedWebcamGlowBatch, type WebcamGlowBatch } from "./WebcamGlowBatch"
import { cCategoryByIndex, cFlagStale, cFlagStream, cTierRadiiKm, type WebcamClusterIndex, type WebcamTier } from "./WebcamClusterIndex"
import {
	cCategoryColors, cDefaultColor, cHitPadding, cMarkerSphereRadius, cStaleColor, cWebcamFarFill, cWebcamFill,
	cWebcamHeightAboveTerrainKm, cWebcamMaxAngular, cWebcamMinAngular, hoverColor, type WebcamNode
} from "./WebcamNode"

const cNoActionSets: RenderActionSet[] = []

// A tier is fine enough when its merge radius spans this many pixels on
// the ground under the view center: items of that tier are then at least
// about this far apart on screen. A little over a cluster chip's width
// with its count on the corner, so neighboring chips clear each other
const cTargetSpacingPx = 52
// Individual cameras (tier 0) are mostly small domes: show them once the
// first merge radius spans this many pixels
const cLeafSpacingPx = 20
// Thumbnail chip half sizes (16:9) in logical px
const cChipHalfW = 18
const cChipHalfH = 10
const cClusterChipHalfW = 22
const cClusterChipHalfH = 12.5
// The hovered chip grows by this factor
const cChipHoverScale = 1.12
// The count on a cluster chip is smaller than a standalone badge and sits
// this far inside the chip's bottom-right corner
const cChipBadgeShrinkPx = 3
const cChipBadgeInsetPx = 3
// Switching tiers requires overshooting the threshold, so a camera
// hovering near a boundary doesn't flip back and forth: going finer needs
// the finer tier to clear its bar by this much, going coarser lets the
// current tier fall only slightly short before it merges (a tier left too
// crowded is the worse failure)
const cHysteresisFiner = 1.25
const cHysteresisCoarser = 1.1
const cTransitionMs = 320
// Items this far off screen are still placed so pans reveal them smoothly
const cViewportMarginPx = 48
// Above this altitude the bbox is unreliable; the chosen tier is coarse
// enough up there to just walk it whole
const cGlobalAltitudeKm = 5000
// Visible single cameras and chip representatives have their rows
// prefetched at this cadence, so a hover or tap can materialize a node
// without waiting on the database and chips get their thumbnail URL
const cPrefetchIntervalMs = 400
const cPrefetchMax = 600
// Smallest pick radius for a dome, in logical px
const cMinPickPx = 7
// Cluster badge: ring color (fuchsia-500, the family anchor) and disc radii by count
const cBadgeColor = rgba(0.85, 0.27, 0.94, 0.95)
const cBadgeStaleColor = rgba(0.62, 0.55, 0.65, 0.9)
const cBadgePickPadPx = 4
// Markers fade out as the sine of the camera's elevation above their local
// horizon falls through this range (fully gone below ~10 degrees, full
// strength above ~30)
const cGrazeFadeStart = 0.18
const cGrazeFadeEnd = 0.5
// Chips and badges are full size when the sine of that elevation is at
// least this, shrinking linearly below it down to this floor
const cForeshortenFullSin = 0.75
const cForeshortenMinScale = 0.5
// Fraction of the half-height a cluster's extent should fill after zooming to it
const cZoomFit = 0.7
const cZoomMinAltitudeKm = 1.5
// A cluster tap always descends to between these fractions of the current
// altitude, so one tap from orbit is a real move but never the whole way
const cZoomMaxDescent = 0.08
const cZoomMinDescent = 0.5

// Planet view: over this altitude band the tier's markers dissolve into a
// density glow plus a featured set of hero chips
const cPlanetFadeStartKm = 3500
const cPlanetFadeEndKm = 6500
// Glow discs come from this tier (~150 km clusters) whatever the display
// tier, so the field has regional texture; capped by the index's depth
const cGlowTier = 9
const cGlowMinRadiusKm = 190
const cGlowRadiusPerExtent = 1.3
// Disc opacity grows with the log of the camera count
const cGlowBaseOpacity = 0.3
const cGlowOpacityPerDecade = 0.12
const cGlowMaxOpacity = 0.65
const cGlowMinPickPx = 14
// Hero chips that would overlap an already-placed (better-scored) one are
// held back; this pads the test so they also keep a little air
const cHeroOverlapPadPx = 6
// Featured cameras: one per cluster of this tier (~600 km, so hero chips
// clear each other from orbit), at most this many, hero chip half size
const cFeaturedTier = 11
const cFeaturedMax = 60
const cHeroChipHalfW = 28
const cHeroChipHalfH = 15.75
// Daylight terms for the featured score (sine of sun elevation)
const cDaylitSin = 0.05
const cGoldenHourSin = 0.3
// Re-pick the featured set when the sun has moved this far (cosine of 2°)
const cFeaturedSunCos = Math.cos(2 * Math.PI / 180)

const cRad = trig.rad

/** What the cloud needs from its layer: camera rows, interactive nodes, camera flights */
export interface WebcamCloudHost {
	/** Cached full row for a leaf, if it has been fetched */
	entryFor(leaf: number): WebcamEntry | undefined
	/** Fetch rows for these leaves in the background (deduplicated) */
	requestEntries(leaves: number[]): void
	/** The interactive node for a leaf, creating it when its row is cached */
	materialize(leaf: number): WebcamNode | undefined
	/** The interactive node for a leaf if one is live (never creates) */
	nodeFor(leaf: number): WebcamNode | undefined
	/** Drop interactive nodes nothing needs any more; `keepLeaf` is the pick candidate (-1 none) */
	pruneMaterialized(keepLeaf: number): void
	/** Focus a leaf, fetching its row first if needed */
	focusLeaf(leaf: number): void
	zoomTo(lat: number, lon: number, altitudeKm: number): void
	/** Open the contact sheet listing a cluster's cameras */
	openSheet(tier: number, index: number): void
}

interface ItemRef {
	tier: number
	index: number
}

function badgeRadiusPx(count: number): number {
	if (count < 10) return 10
	if (count < 100) return 12
	if (count < 1000) return 14
	return 16
}

/** Thumbnail URL as the hover card and browser request it, so the browser cache is shared */
function thumbUrl(entry: WebcamEntry): string {
	const stamp = entry.last_success ? new Date(entry.last_success).getTime() : 0
	return `${datasetUrl(entry.thumb_url)}?eb=${stamp}`
}

export class WebcamCloudNode extends GestureNode {
	public readonly id = "webcam-cloud"
	public readonly focusable = false

	private readonly host: WebcamCloudHost
	private readonly ebContext: EarthBrowserContext
	private readonly domes: EmberDomeBatch
	private readonly badges: WebcamBadgeBatch
	private readonly chips: WebcamChipBatch
	private readonly glows: WebcamGlowBatch
	private index?: WebcamClusterIndex

	// Planet view: 0 = the tier's markers, 1 = glow + featured chips
	private planetBlend = 0
	private glowCount = 0
	// Featured leaves and the sun direction they were picked for
	private featured: number[] = []
	private featuredSun?: { x: number, y: number, z: number }
	private featuredIndex?: WebcamClusterIndex
	// Featured candidates whose thumbnail turned out to be a night frame
	private readonly darkLeaves = new Set<number>()
	// Screen centers of the hero chips placed this frame (x, y pairs)
	private readonly heroRects: number[] = []

	// Tier state
	private tier = -1
	private fromTier = -1
	private transitionStart = 0
	private blend = 1

	// Per-tier caches: terrain elevation (km, NaN = unknown) and packed badge text
	private elevation: Float32Array[] = []
	private packed: Uint32Array[] = []
	private elevationStamp = ""

	// Items drawn last frame, for picking
	private visCount = 0
	private visTier = new Uint8Array(1024)
	private visIndex = new Int32Array(1024)
	private visSx = new Float32Array(1024)
	private visSy = new Float32Array(1024)
	private visPickPx = new Float32Array(1024)
	private visWorld = new Float32Array(1024 * 3)
	private domeCount = 0
	private badgeCount = 0
	private chipCount = 0
	// Set while any chip's thumbnail is still fading in
	private thumbsFading = false

	private candidate?: ItemRef
	private hovered?: ItemRef
	private lastPrefetchAt = 0
	private readonly contextMenuListener: (event: MouseEvent) => void
	// CPU time of the last render pass (tier choice, culling, batch appends)
	private lastFrameMs = 0

	// Last frame's view scale, for pick radii and zoom-to-cluster
	private lastPxPerKm = 1
	// 1 / tan(fov / 2), and that times half the viewport height in px
	private lastFocal = 2.75
	private lastHalfHeightFocal = 1
	private lastAltitudeKm = 1

	// Scratch reused across frames
	private readonly scratchA: number[] = []
	private readonly scratchB: number[] = []
	private readonly skipSet = new Set<number>()
	private readonly fullSet = new Set<number>()
	private readonly look: EmberDomeAppearance = {
		color: cDefaultColor, opacity: 1, pulse: 0, fill: cWebcamFill, farFill: cWebcamFarFill, minAngular: cWebcamMinAngular
	}

	constructor(context: EarthBrowserContext, host: WebcamCloudHost) {
		super(context)
		this.host = host
		this.ebContext = context
		this.domes = sharedEmberDomeBatch(context)
		this.badges = sharedWebcamBadgeBatch(context)
		this.chips = sharedWebcamChipBatch(context)
		this.glows = sharedWebcamGlowBatch(context)

		// Hover highlights the item under the pointer; a hovered camera
		// whose row isn't cached yet gets it fetched so the next pointer
		// move can hand the hit to a real node
		const hover = new HoverGestureRecognizer()
		hover.priority = 5
		hover.onGesture(data => {
			switch (data.state) {
				case GestureState.Began:
				case GestureState.Changed:
					this.setHovered(this.candidate)
					break
				case GestureState.Ended:
				case GestureState.Cancelled:
					this.setHovered(undefined)
					break
			}
		})
		this.addRecognizer(hover)

		const tap = new TapGestureRecognizer({ maximumTouches: 1 })
		tap.priority = 20
		tap.onGesture(() => this.activate(this.candidate))
		this.addRecognizer(tap)

		// Press-and-hold on a cluster opens its contact sheet (the drop-pin
		// long press on the globe underneath loses to this one's priority)
		const longPress = new LongPressGestureRecognizer({ maximumTouches: 1 })
		longPress.priority = 20
		longPress.onGesture(data => {
			if (data.state === GestureState.Began) this.openSheet(this.candidate)
		})
		this.addRecognizer(longPress)

		// Right-click on a cluster does the same. The gesture system has no
		// contextmenu path, so pick directly; capture phase so this runs
		// before Earth's drop-pin listener and can claim the event
		this.contextMenuListener = (event: MouseEvent) => {
			if (!this.visible || !this.index) return
			const rect = this.ebContext.canvas.getBoundingClientRect()
			const k = this.pickAt(event.clientX - rect.left, event.clientY - rect.top)
			if (k < 0) return
			const ref = { tier: this.visTier[k], index: this.visIndex[k] }
			if (this.index.tiers[ref.tier].size[ref.index] <= 1) return
			event.preventDefault()
			event.stopImmediatePropagation()
			this.openSheet(ref)
		}
		this.ebContext.canvas.addEventListener("contextmenu", this.contextMenuListener, { capture: true })
	}

	override destroy(): void {
		this.ebContext.canvas.removeEventListener("contextmenu", this.contextMenuListener, { capture: true })
		super.destroy()
	}

	private openSheet(ref: ItemRef | undefined): void {
		const index = this.index
		if (!ref || !index) return
		if (index.tiers[ref.tier].size[ref.index] <= 1) return
		this.host.openSheet(ref.tier, ref.index)
	}

	public setIndex(index: WebcamClusterIndex | undefined): void {
		this.index = index
		this.tier = -1
		this.fromTier = -1
		this.blend = 1
		this.elevation = []
		this.packed = []
		this.elevationStamp = ""
		this.visCount = 0
		this.candidate = undefined
		this.hovered = undefined
		this.chips.atlas.reset()
		this.featured = []
		this.featuredIndex = undefined
		this.darkLeaves.clear()
		requestRender()
	}

	/** Leaf of the current pick candidate, or -1 */
	public get candidateLeaf(): number {
		const c = this.candidate
		if (!c || !this.index) return -1
		const tier = this.index.tiers[c.tier]
		return tier.size[c.index] === 1 ? tier.leaf[c.index] : -1
	}

	/** Diagnostics for window.__ebDebug.getWebcams() */
	public snapshot(): Record<string, unknown> {
		// A few drawn items with their screen positions, so automation can
		// aim pointer events at a cluster or a camera
		const sample: Array<{ tier: number, index: number, size: number, leaf: number, x: number, y: number, pickPx: number }> = []
		const index = this.index
		for (let k = 0; k < this.visCount && sample.length < 12; k += Math.max(1, Math.floor(this.visCount / 12))) {
			const tier = index?.tiers[this.visTier[k]]
			if (!tier) break
			sample.push({
				tier: this.visTier[k], index: this.visIndex[k], size: tier.size[this.visIndex[k]], leaf: tier.leaf[this.visIndex[k]],
				x: this.visSx[k], y: this.visSy[k], pickPx: this.visPickPx[k],
			})
		}
		return {
			sample,
			tier: this.tier,
			fromTier: this.fromTier,
			blend: this.blend,
			tierRadiusKm: this.tier >= 0 ? cTierRadiiKm[this.tier] : null,
			pxPerKm: this.lastPxPerKm,
			frameMs: this.lastFrameMs,
			visible: this.visCount,
			domes: this.domeCount,
			badges: this.badgeCount,
			chips: this.chipCount,
			planetBlend: this.planetBlend,
			glows: this.glowCount,
			featured: this.featured.length,
			featuredIds: this.index ? this.featured.map(leaf => this.index!.ids[leaf]) : [],
			darkFeatured: this.darkLeaves.size,
			atlas: this.chips.atlas.stats,
			hovered: this.hovered ? { ...this.hovered } : null,
			candidate: this.candidate ? { ...this.candidate } : null,
		}
	}

	// ==================== Rendering ====================

	public override render(state: RenderState): RenderActionSet[] {
		const index = this.index
		if (!this.visible || !index) {
			this.visCount = 0
			return cNoActionSets
		}

		const now = performance.now()
		this.refreshElevationCache(index)

		const pxPerKm = this.groundPxPerKm(state)
		this.lastPxPerKm = pxPerKm
		const next = this.chooseTier(index, pxPerKm)
		if (next !== this.tier) {
			if (this.tier >= 0) {
				this.fromTier = this.tier
				this.transitionStart = now
			}
			this.tier = next
		}
		if (this.fromTier >= 0) {
			const t = Math.min(1, (now - this.transitionStart) / cTransitionMs)
			this.blend = t * t * (3 - 2 * t)
			if (t >= 1) {
				this.fromTier = -1
				this.blend = 1
			} else {
				requestRender()
			}
		}

		this.host.pruneMaterialized(this.candidateLeaf)
		this.thumbsFading = false
		this.collect(state, index)
		if (this.thumbsFading) requestRender()
		this.lastFrameMs = performance.now() - now

		if (now - this.lastPrefetchAt >= cPrefetchIntervalMs) {
			this.lastPrefetchAt = now
			this.prefetchVisibleLeaves(index)
		}
		return cNoActionSets
	}

	/**
	 * Logical pixels per ground kilometer under the view center: the
	 * perspective scale at the point the center ray meets the sphere (or
	 * at the camera altitude when the center ray misses the globe)
	 */
	private groundPxPerKm(state: RenderState): number {
		const altitude = Math.max(0.1, state.camera.position.length - wgs.a)
		this.lastAltitudeKm = altitude
		// The ray's own origin sits on the near plane; measure from the eye
		const o = state.camera.position
		const d = state.camera.rayFrom(state.width * 0.5, state.height * 0.5).direction
		const b = o.x * d.x + o.y * d.y + o.z * d.z
		const c = o.x * o.x + o.y * o.y + o.z * o.z - wgs.a2
		const disc = b * b - c
		let distance = altitude
		if (disc >= 0) {
			const t = -b - Math.sqrt(disc)
			if (t > 0) distance = t
		}
		// projection[5] = 1 / tan(fov / 2): half the viewport height in
		// pixels per unit of tan(angle)
		const focal = state.projection.glArrayAccess()[5]
		this.lastFocal = focal
		this.lastHalfHeightFocal = state.height * 0.5 * focal
		return this.lastHalfHeightFocal / distance
	}

	private chooseTier(index: WebcamClusterIndex, pxPerKm: number): number {
		const tiers = index.tierCount
		// Tier t is fine enough when its items are far enough apart on
		// screen: its own merge radius for cluster tiers, the first merge
		// radius (what tier 0 un-merges) for individual cameras
		const fineEnough = (t: number, overshoot: number): boolean => {
			const radiusKm = t === 0 ? cTierRadiiKm[1] : cTierRadiiKm[t]
			const spacing = t === 0 ? cLeafSpacingPx : cTargetSpacingPx
			return radiusKm * pxPerKm >= spacing * overshoot
		}
		// The finest tier that qualifies. Not a monotonic walk: tier 0 has a
		// looser bar than tier 1, so a scan from the finest end is needed
		const finest = (overshoot: number): number => {
			for (let t = 0; t < tiers; t++) {
				if (fineEnough(t, overshoot)) return t
			}
			return tiers - 1
		}
		if (this.tier < 0 || this.tier >= tiers) return finest(1)
		// Go finer only when the finer tier clears its bar with room to
		// spare; go coarser only when the current one fails even a lenient bar
		const finer = finest(cHysteresisFiner)
		if (finer < this.tier) return finer
		const lenient = finest(1 / cHysteresisCoarser)
		if (lenient > this.tier) return lenient
		return this.tier
	}

	private refreshElevationCache(index: WebcamClusterIndex): void {
		const context = this.ebContext
		const stamp = `${context.terrainVersion}|${context.baseElevation.loaded ? 1 : 0}`
		if (stamp === this.elevationStamp && this.elevation.length === index.tierCount) return
		this.elevationStamp = stamp
		this.elevation = index.tiers.map(tier => new Float32Array(tier.count).fill(Number.NaN))
		if (this.packed.length !== index.tierCount) {
			this.packed = index.tiers.map(tier => new Uint32Array(tier.count))
		}
	}

	private elevationKm(tierIndex: number, tier: WebcamTier, i: number): number {
		const cache = this.elevation[tierIndex]
		let e = cache[i]
		if (Number.isNaN(e)) {
			e = this.ebContext.displayElevationKm(new geographic(tier.lon[i] * cRad, tier.lat[i] * cRad))
			cache[i] = e
		}
		return e
	}

	private packedCount(tierIndex: number, tier: WebcamTier, i: number): number {
		const cache = this.packed[tierIndex]
		let p = cache[i]
		if (p === 0) {
			p = packCount(tier.size[i])
			cache[i] = p
		}
		return p
	}

	/** Gather a tier's candidate items for the current view into `out` */
	private gather(tier: WebcamTier, bbox: [number, number, number, number] | null, out: number[]): void {
		out.length = 0
		if (bbox) tier.collectInBBox(bbox, out)
		else tier.collectAll(out)
	}

	/** Build this frame's visible set and feed the batches */
	private collect(state: RenderState, index: WebcamClusterIndex): void {
		this.visCount = 0
		this.domeCount = 0
		this.badgeCount = 0
		this.chipCount = 0
		this.glowCount = 0
		const bbox = this.lastAltitudeKm > cGlobalAltitudeKm ? null : computeViewportBBox(state, cViewportMarginPx)
		if (bbox === null && this.lastAltitudeKm <= cGlobalAltitudeKm) {
			return // globe not in view
		}

		// Planet view: the tier's markers give way to the glow + featured set
		const p = Math.min(1, Math.max(0, (this.lastAltitudeKm - cPlanetFadeStartKm) / (cPlanetFadeEndKm - cPlanetFadeStartKm)))
		this.planetBlend = p * p * (3 - 2 * p)
		if (this.planetBlend > 0) {
			this.collectPlanet(state, index, bbox)
		}
		const tierAlpha = 1 - this.planetBlend
		if (tierAlpha <= 0.003) return

		const to = this.tier
		const from = this.fromTier
		const toTier = index.tiers[to]
		const t = this.blend

		if (from < 0) {
			this.gather(toTier, bbox, this.scratchA)
			for (const i of this.scratchA) {
				this.emit(state, index, to, i, toTier.nx[i], toTier.ny[i], toTier.nz[i], tierAlpha)
			}
			return
		}

		const fromTier = index.tiers[from]
		const skip = this.skipSet
		const full = this.fullSet
		skip.clear()
		full.clear()

		if (from > to) {
			// Zooming in: finer items grow out of the coarser item that held
			// them; items unchanged between the tiers are drawn once
			this.gather(toTier, bbox, this.scratchA)
			for (const i of this.scratchA) {
				const a = index.ancestor(to, i, from)
				if (a >= 0 && fromTier.size[a] === toTier.size[i]) {
					skip.add(a)
					this.emit(state, index, to, i, toTier.nx[i], toTier.ny[i], toTier.nz[i], tierAlpha)
				} else if (a >= 0) {
					this.emitLerped(state, index, to, i, fromTier.nx[a], fromTier.ny[a], fromTier.nz[a], toTier.nx[i], toTier.ny[i], toTier.nz[i], t, t * tierAlpha)
				} else {
					this.emit(state, index, to, i, toTier.nx[i], toTier.ny[i], toTier.nz[i], t * tierAlpha)
				}
			}
			this.gather(fromTier, bbox, this.scratchB)
			for (const a of this.scratchB) {
				if (skip.has(a)) continue
				this.emit(state, index, from, a, fromTier.nx[a], fromTier.ny[a], fromTier.nz[a], (1 - t) * tierAlpha)
			}
		} else {
			// Zooming out: finer items slide into the cluster absorbing them
			// while it fades in
			this.gather(fromTier, bbox, this.scratchB)
			for (const f of this.scratchB) {
				const p = index.ancestor(from, f, to)
				if (p >= 0 && toTier.size[p] === fromTier.size[f]) {
					full.add(p)
					continue
				}
				if (p >= 0) {
					this.emitLerped(state, index, from, f, fromTier.nx[f], fromTier.ny[f], fromTier.nz[f], toTier.nx[p], toTier.ny[p], toTier.nz[p], t, (1 - t) * tierAlpha)
				} else {
					this.emit(state, index, from, f, fromTier.nx[f], fromTier.ny[f], fromTier.nz[f], (1 - t) * tierAlpha)
				}
			}
			this.gather(toTier, bbox, this.scratchA)
			for (const i of this.scratchA) {
				this.emit(state, index, to, i, toTier.nx[i], toTier.ny[i], toTier.nz[i], (full.has(i) ? 1 : t) * tierAlpha)
			}
		}
	}

	// ==================== Planet View ====================

	/**
	 * From orbit the catalog reads as a density glow (one soft ground disc
	 * per regional cluster, opacity by camera count) plus a featured set of
	 * hero chips: the best daylit non-traffic camera of each ~600 km region
	 */
	private collectPlanet(state: RenderState, index: WebcamClusterIndex, bbox: [number, number, number, number] | null): void {
		const blend = this.planetBlend
		const glowTierIndex = Math.min(cGlowTier, index.tierCount - 1)
		const glowTier = index.tiers[glowTierIndex]
		const cam = state.camera.position
		const m = state.modelViewProjection.glArrayAccess()

		this.gather(glowTier, bbox, this.scratchA)
		for (const i of this.scratchA) {
			const nx = glowTier.nx[i]
			const ny = glowTier.ny[i]
			const nz = glowTier.nz[i]
			if (nx * cam.x + ny * cam.y + nz * cam.z <= wgs.a) continue
			const r = wgs.a + this.elevationKm(glowTierIndex, glowTier, i)
			const wx = nx * r
			const wy = ny * r
			const wz = nz * r
			const cw = m[3] * wx + m[7] * wy + m[11] * wz + m[15]
			if (cw <= 0) continue
			const ndcX = (m[0] * wx + m[4] * wy + m[8] * wz + m[12]) / cw
			const ndcY = (m[1] * wx + m[5] * wy + m[9] * wz + m[13]) / cw
			if (ndcX < -1.2 || ndcX > 1.2 || ndcY < -1.2 || ndcY > 1.2) continue

			const dx = wx - cam.x
			const dy = wy - cam.y
			const dz = wz - cam.z
			const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
			const sinElevation = -(nx * dx + ny * dy + nz * dz) / distance
			const graze = Math.min(1, Math.max(0, (sinElevation - cGrazeFadeStart) / (cGrazeFadeEnd - cGrazeFadeStart)))
			if (graze <= 0) continue

			const size = glowTier.size[i]
			const radiusKm = Math.max(cGlowMinRadiusKm, glowTier.extentKm[i] * cGlowRadiusPerExtent)
			const opacity = Math.min(cGlowMaxOpacity, cGlowBaseOpacity + Math.log10(size) * cGlowOpacityPerDecade) * blend * graze
			this.glows.add(state, wx, wy, wz, nx, ny, nz, radiusKm, opacity)
			this.glowCount++

			// Pickable over roughly the disc's bright core, so a tap on the
			// glow zooms to that region and a long press lists its cameras
			const sx = (ndcX + 1) * state.width * 0.5
			const sy = (1 - ndcY) * state.height * 0.5
			const pickPx = Math.max(cGlowMinPickPx, radiusKm * 0.6 / distance * this.lastHalfHeightFocal)
			this.recordVisible(glowTierIndex, i, sx, sy, pickPx, wx, wy, wz)
		}

		this.refreshFeatured(index)
		const leaves = index.tiers[0]
		const atlas = this.chips.atlas
		const placed = this.heroRects
		placed.length = 0
		let demoted = false
		for (const leaf of this.featured) {
			// A frame that decoded black is a night capture: drop it from the
			// set and let the region's runner-up take the slot next frame
			if (atlas.isDark(leaf)) {
				this.darkLeaves.add(leaf)
				demoted = true
				continue
			}
			const nx = leaves.nx[leaf]
			const ny = leaves.ny[leaf]
			const nz = leaves.nz[leaf]
			if (nx * cam.x + ny * cam.y + nz * cam.z <= wgs.a) continue
			const r = wgs.a + this.elevationKm(0, leaves, leaf) + cWebcamHeightAboveTerrainKm
			const wx = nx * r
			const wy = ny * r
			const wz = nz * r
			const cw = m[3] * wx + m[7] * wy + m[11] * wz + m[15]
			if (cw <= 0) continue
			const sx = ((m[0] * wx + m[4] * wy + m[8] * wz + m[12]) / cw + 1) * state.width * 0.5
			const sy = (1 - (m[1] * wx + m[5] * wy + m[9] * wz + m[13]) / cw) * state.height * 0.5
			// Hero chips are placed best-first; one that would land on an
			// earlier chip waits for a view where it has room
			const halfW = cHeroChipHalfW + cHeroOverlapPadPx
			const halfH = cHeroChipHalfH + cHeroOverlapPadPx
			let overlaps = false
			for (let k = 0; k < placed.length; k += 2) {
				if (Math.abs(placed[k] - sx) < halfW * 2 && Math.abs(placed[k + 1] - sy) < halfH * 2) {
					overlaps = true
					break
				}
			}
			if (overlaps) continue
			placed.push(sx, sy)
			this.emit(state, index, 0, leaf, nx, ny, nz, blend, true)
		}
		if (demoted) {
			this.featuredSun = undefined
			requestRender()
		}
	}

	/**
	 * Pick the featured set: per cFeaturedTier region, the best non-traffic
	 * healthy camera, daylit ones first (a webcam of the night is a black
	 * rectangle), golden hour and live streams ahead of the rest. Re-picked
	 * when the catalog changes or the sun has moved a couple of degrees, so
	 * the set follows the terminator as the clock runs
	 */
	private refreshFeatured(index: WebcamClusterIndex): void {
		const julian = this.ebContext.clock.clockTime.julian
		const sun = celestialToWorldVector(sunPosition(julian).direction, greenwichSiderealTime(julian))
		const previous = this.featuredSun
		if (
			this.featuredIndex === index && previous &&
			previous.x * sun.x + previous.y * sun.y + previous.z * sun.z >= cFeaturedSunCos
		) {
			return
		}
		this.featuredIndex = index
		this.featuredSun = { x: sun.x, y: sun.y, z: sun.z }

		const leaves = index.tiers[0]
		const regionTier = Math.min(cFeaturedTier, index.tierCount - 1)
		const best = new Map<number, { leaf: number, score: number }>()
		for (let leaf = 0; leaf < leaves.count; leaf++) {
			if (index.category[leaf] === 0 || (index.flags[leaf] & cFlagStale) || this.darkLeaves.has(leaf)) continue
			const sinSun = leaves.nx[leaf] * sun.x + leaves.ny[leaf] * sun.y + leaves.nz[leaf] * sun.z
			let score = leaves.rank[leaf]
			if (sinSun > cDaylitSin) score += 100
			if (sinSun > cDaylitSin && sinSun < cGoldenHourSin) score += 3
			if (index.flags[leaf] & cFlagStream) score += 5
			const region = index.ancestor(0, leaf, regionTier)
			const current = best.get(region)
			if (!current || score > current.score) best.set(region, { leaf, score })
		}
		this.featured = [...best.values()]
			.sort((a, b) => b.score - a.score)
			.slice(0, cFeaturedMax)
			.map(entry => entry.leaf)
		requestRender()
	}

	/** Emit an item part way between two surface normals (renormalized lerp) */
	private emitLerped(
		state: RenderState, index: WebcamClusterIndex, tierIndex: number, i: number,
		ax: number, ay: number, az: number, bx: number, by: number, bz: number, t: number, alpha: number
	): void {
		let x = ax + (bx - ax) * t
		let y = ay + (by - ay) * t
		let z = az + (bz - az) * t
		const len = Math.hypot(x, y, z) || 1
		x /= len
		y /= len
		z /= len
		this.emit(state, index, tierIndex, i, x, y, z, alpha)
	}

	/**
	 * Cull one item against the horizon and the viewport, then append it to
	 * the dome or badge batch and record it for picking
	 */
	private emit(state: RenderState, index: WebcamClusterIndex, tierIndex: number, i: number, nx: number, ny: number, nz: number, alpha: number, hero = false): void {
		if (alpha <= 0.003) return
		const tier = index.tiers[tierIndex]
		const cam = state.camera.position
		// Horizon: a surface point faces the camera iff n . cam > a
		if (nx * cam.x + ny * cam.y + nz * cam.z <= wgs.a) return

		const size = tier.size[i]
		const leaf = tier.leaf[i]
		const traffic = index.category[leaf] === 0
		// A traffic camera with a live WebcamNode (hovered, focused,
		// deep-linked) has its dome drawn by that node; a chip camera's node
		// draws nothing and only lends its hover / focus state below
		let nodeHovered = false
		let nodeOpacity = 1
		if (size === 1) {
			const node = this.host.nodeFor(leaf)
			if (node) {
				if (traffic) return
				nodeHovered = node.isHovered
				nodeOpacity = node.markerOpacity
			}
		}
		const r = wgs.a + this.elevationKm(tierIndex, tier, i) + cWebcamHeightAboveTerrainKm
		const wx = nx * r
		const wy = ny * r
		const wz = nz * r

		const m = state.modelViewProjection.glArrayAccess()
		const cw = m[3] * wx + m[7] * wy + m[11] * wz + m[15]
		if (cw <= 0) return
		const ndcX = (m[0] * wx + m[4] * wy + m[8] * wz + m[12]) / cw
		const ndcY = (m[1] * wx + m[5] * wy + m[9] * wz + m[13]) / cw
		const mx = cViewportMarginPx * 2 / state.width
		const my = cViewportMarginPx * 2 / state.height
		if (ndcX < -1 - mx || ndcX > 1 + mx || ndcY < -1 - my || ndcY > 1 + my) return

		const sx = (ndcX + 1) * state.width * 0.5
		const sy = (1 - ndcY) * state.height * 0.5
		const dx = wx - cam.x
		const dy = wy - cam.y
		const dz = wz - cam.z
		const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
		// Grazing-angle fade: the tier's spacing holds under the view
		// center, but toward the limb (or the far ground of a tilted view)
		// foreshortening stacks markers up, so they dissolve as the camera's
		// elevation above their local horizon drops below ~30 degrees
		const sinElevation = -(nx * dx + ny * dy + nz * dz) / distance
		const graze = (sinElevation - cGrazeFadeStart) / (cGrazeFadeEnd - cGrazeFadeStart)
		if (graze < 1) {
			if (graze <= 0) return
			alpha *= graze
		}
		// Fixed-pixel chips and badges also shrink as the ground tilts away,
		// so a hemisphere's worth of clusters recedes toward the limb
		// instead of stacking at full size
		const foreshorten = Math.min(1, Math.max(cForeshortenMinScale, sinElevation / cForeshortenFullSin))
		const hovered = nodeHovered || (this.hovered?.tier === tierIndex && this.hovered.index === i)
		alpha *= nodeOpacity
		const flags = index.flags[leaf]
		const stale = (flags & cFlagStale) !== 0
		const base: color = stale ? cStaleColor : cCategoryColors[cCategoryByIndex[index.category[leaf]]] ?? cDefaultColor

		// Traffic cameras are infrastructure: a dome alone, or a plain count
		// badge for an all-traffic cluster. Anything else gets its picture
		let pickPx: number
		if (!traffic) {
			const now = performance.now()
			const scale = (hovered ? cChipHoverScale : 1) * foreshorten
			const halfW = (hero ? cHeroChipHalfW : size === 1 ? cChipHalfW : cClusterChipHalfW) * scale
			const halfH = (hero ? cHeroChipHalfH : size === 1 ? cChipHalfH : cClusterChipHalfH) * scale
			pickPx = halfW + cBadgePickPadPx
			let u0 = 0, v0 = 0, u1 = 0, v1 = 0, fade = 0
			const placement = this.chips.atlas.lookup(leaf, now)
			if (placement) {
				u0 = placement.u0
				v0 = placement.v0
				u1 = placement.u1
				v1 = placement.v1
				fade = placement.fade
				if (fade < 1) this.thumbsFading = true
			} else if (!this.chips.atlas.hasFailed(leaf)) {
				// Needs the row for its thumbnail URL; the prefetch pass
				// fetches rows for visible chips, so this is usually cached
				const entry = this.host.entryFor(leaf)
				if (entry) this.chips.atlas.request(leaf, thumbUrl(entry), now)
			}
			const chipFlags = ((flags & cFlagStream) ? cChipFlagLive : 0) | (stale ? cChipFlagStale : 0)
			this.chips.add(
				state, wx, wy, wz, halfW, halfH, u0, v0, u1, v1,
				hovered ? hoverColor(base) : base, alpha, fade, chipFlags, hovered ? 1 : 0
			)
			this.chipCount++
			if (size > 1) {
				const radiusPx = (badgeRadiusPx(size) - cChipBadgeShrinkPx) * foreshorten
				this.badges.add(
					state, wx, wy, wz, radiusPx, cBadgeColor, alpha, this.packedCount(tierIndex, tier, i), hovered ? 1 : 0,
					halfW - cChipBadgeInsetPx, halfH - cChipBadgeInsetPx
				)
			}
		} else if (size === 1) {
			const radiusKm = Math.min(cMarkerSphereRadius, distance * cWebcamMaxAngular)
			const apparentKm = Math.max(radiusKm, distance * cWebcamMinAngular)
			pickPx = Math.max(cMinPickPx, apparentKm / distance * this.lastHalfHeightFocal * cHitPadding)
			const look = this.look
			look.color = hovered ? hoverColor(base) : base
			look.fill = hovered ? 1 : cWebcamFill
			look.opacity = alpha
			this.domes.addPoint(state, wx, wy, wz, radiusKm, look)
			this.domeCount++
		} else {
			const radiusPx = badgeRadiusPx(size) * foreshorten
			pickPx = radiusPx + cBadgePickPadPx
			const ring = stale ? cBadgeStaleColor : cBadgeColor
			this.badges.add(state, wx, wy, wz, radiusPx, ring, alpha, this.packedCount(tierIndex, tier, i), hovered ? 1 : 0)
			this.badgeCount++
		}

		this.recordVisible(tierIndex, i, sx, sy, pickPx, wx, wy, wz)
	}

	private recordVisible(tierIndex: number, i: number, sx: number, sy: number, pickPx: number, wx: number, wy: number, wz: number): void {
		const k = this.visCount
		if (k >= this.visIndex.length) {
			const n = this.visIndex.length * 2
			const grow = <T extends Float32Array | Int32Array | Uint8Array>(a: T, len: number): T => {
				const b = new (a.constructor as new (n: number) => T)(len)
				b.set(a)
				return b
			}
			this.visTier = grow(this.visTier, n)
			this.visIndex = grow(this.visIndex, n)
			this.visSx = grow(this.visSx, n)
			this.visSy = grow(this.visSy, n)
			this.visPickPx = grow(this.visPickPx, n)
			this.visWorld = grow(this.visWorld, n * 3)
		}
		this.visTier[k] = tierIndex
		this.visIndex[k] = i
		this.visSx[k] = sx
		this.visSy[k] = sy
		this.visPickPx[k] = pickPx
		this.visWorld[k * 3] = wx
		this.visWorld[k * 3 + 1] = wy
		this.visWorld[k * 3 + 2] = wz
		this.visCount = k + 1
	}

	private removeVisible(k: number): void {
		const last = this.visCount - 1
		if (k !== last) {
			this.visTier[k] = this.visTier[last]
			this.visIndex[k] = this.visIndex[last]
			this.visSx[k] = this.visSx[last]
			this.visSy[k] = this.visSy[last]
			this.visPickPx[k] = this.visPickPx[last]
			this.visWorld[k * 3] = this.visWorld[last * 3]
			this.visWorld[k * 3 + 1] = this.visWorld[last * 3 + 1]
			this.visWorld[k * 3 + 2] = this.visWorld[last * 3 + 2]
		}
		this.visCount = last
	}

	/**
	 * Ask the host for the rows that on-screen items will need and don't
	 * have cached: every single camera (for hover / tap) and every chip's
	 * representative (for its thumbnail URL)
	 */
	private prefetchVisibleLeaves(index: WebcamClusterIndex): void {
		const want: number[] = []
		for (let k = 0; k < this.visCount && want.length < cPrefetchMax; k++) {
			const tier = index.tiers[this.visTier[k]]
			const i = this.visIndex[k]
			const leaf = tier.leaf[i]
			if (tier.size[i] !== 1 && index.category[leaf] === 0) continue
			if (!this.host.entryFor(leaf)) want.push(leaf)
		}
		if (want.length > 0) this.host.requestEntries(want)
	}

	// ==================== Picking & Interaction ====================

	/**
	 * Nearest drawn item to the pointer within its pick radius. Cameras with
	 * a cached row become a real node and the hit is reported on that node,
	 * so its own recognizers (hover card, tap-to-focus) take over.
	 */
	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const index = this.index
		const px = state.mouseX
		const py = state.mouseY
		if (!this.visible || !index || px === undefined || py === undefined) {
			this.candidate = undefined
			return undefined
		}

		const best = this.pickAt(px, py)
		if (best < 0) {
			this.candidate = undefined
			return undefined
		}

		const tierIndex = this.visTier[best]
		const i = this.visIndex[best]
		const tier = index.tiers[tierIndex]
		this.candidate = { tier: tierIndex, index: i }

		const hitPoint = new vec3(this.visWorld[best * 3], this.visWorld[best * 3 + 1], this.visWorld[best * 3 + 2])
		const cameraDistance = hitPoint.subtract(state.camera.position).length
		// Bias by the miss so overlapping neighbors resolve to the one
		// nearest the cursor, as WebcamNode does with its ray test
		const dxp = this.visSx[best] - px
		const dyp = this.visSy[best] - py
		const missKm = Math.sqrt(dxp * dxp + dyp * dyp) * cameraDistance / this.lastHalfHeightFocal
		const distance = cameraDistance + missKm

		if (tier.size[i] === 1) {
			const leaf = tier.leaf[i]
			const node = this.host.materialize(leaf)
			if (node) {
				// A traffic camera's node draws its own dome from now on; a
				// chip stays drawn here and keeps answering hits for the node
				if (index.category[leaf] === 0) this.removeVisible(best)
				return { node, hitPoint, distance }
			}
		}
		return { node: this, hitPoint, distance }
	}

	/** Index into the drawn set of the nearest item within its pick radius of a CSS-px point, or -1 */
	private pickAt(px: number, py: number): number {
		let best = -1
		let bestD2 = Number.POSITIVE_INFINITY
		for (let k = 0; k < this.visCount; k++) {
			const dx = this.visSx[k] - px
			const dy = this.visSy[k] - py
			const d2 = dx * dx + dy * dy
			const r = this.visPickPx[k]
			if (d2 <= r * r && d2 < bestD2) {
				bestD2 = d2
				best = k
			}
		}
		return best
	}

	private setHovered(ref: ItemRef | undefined): void {
		const changed = (ref?.tier !== this.hovered?.tier) || (ref?.index !== this.hovered?.index)
		this.hovered = ref
		if (ref && this.index) {
			const tier = this.index.tiers[ref.tier]
			if (tier.size[ref.index] === 1 && !this.host.entryFor(tier.leaf[ref.index])) {
				this.host.requestEntries([tier.leaf[ref.index]])
			}
		}
		if (changed) requestRender()
	}

	private activate(ref: ItemRef | undefined): void {
		const index = this.index
		if (!ref || !index) return
		const tier = index.tiers[ref.tier]
		if (tier.size[ref.index] === 1) {
			this.host.focusLeaf(tier.leaf[ref.index])
			return
		}
		// Fly to frame the cluster: its extent fills cZoomFit of the
		// half-height, and always at most half the current altitude so the
		// tap visibly descends even for a spatially tiny cluster
		const extentKm = Math.max(tier.extentKm[ref.index], cTierRadiiKm[ref.tier] * 0.5)
		let altitudeKm = extentKm * this.lastFocal / cZoomFit
		altitudeKm = Math.min(altitudeKm, this.lastAltitudeKm * cZoomMinDescent)
		altitudeKm = Math.max(altitudeKm, this.lastAltitudeKm * cZoomMaxDescent, cZoomMinAltitudeKm)
		this.host.zoomTo(tier.lat[ref.index], tier.lon[ref.index], altitudeKm)
	}
}
